import React, { useState, useEffect, useRef } from 'react'; // eslint-disable-line
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/useAuthStore'; // Importamos el store de Zustand
import TopBar from './TopBar';
import NavbarLogo from './NavbarLogo'; // 🟢 Importamos el nuevo componente de logo
import { useFinanceStore } from '../../stores/useFinanceStore'; // 🟢 2. Importamos los stores
import { useInteractionStore } from '../../stores/useInteractionStore';
import { useUIStore } from '../../stores/useUIStore';
import { useDataStore } from '../../stores/useDataStore';
import NotificationDropdown from './NotificationDropdown';
import AdminNotificationDropdown from './AdminNotificationDropdown';
import CreatorActivationOverlay from '../common/CreatorActivationOverlay';
import AgoraProvisioningTransition from '../common/AgoraProvisioningTransition';
import CreatorIdentityProvisioningModal from '../common/CreatorIdentityProvisioningModal';
import { getMediaUrl, DEFAULT_AVATAR_URL, withImageFallback } from '../../utils/imageUtils';
import useDebounce from '../../hooks/useDebounce';
import { useThemeStore } from '../../stores/themeStore'; // 🟢 Importamos el store del tema

const identityLabels = {
  NEWS_JOURNALIST: 'Informador',
  MASTER_EDUCATOR: 'Maestro',
  LIFESTYLE_INFLUENCER: 'Influencer',
  PRO_SPECIALIST: 'Especialista',
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const Navbar = () => {
  // SOLUCIÓN: Seleccionamos cada pieza del estado por separado para evitar bucles infinitos.
  const currentUser = useAuthStore((state) => state.currentUser);
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const logout = useAuthStore((state) => state.logout);
  const unreadCount = useInteractionStore((state) => state.unreadCount);
  const notifications = useInteractionStore((state) => state.notifications);
  const fetchNotifications = useInteractionStore((state) => state.fetchNotifications);
  const theme = useThemeStore((state) => state.theme);
  const toggleTheme = useThemeStore((state) => state.toggleTheme);

  // 🟢 SOLUCIÓN: Cargar notificaciones al iniciar sesión
  useEffect(() => {
    if (isLoggedIn && currentUser?._id && fetchNotifications) {
      fetchNotifications();
    }
  }, [isLoggedIn, currentUser?._id]);

  const searchTerm = useUIStore((state) => state.search.searchTerm);
  const suggestions = useUIStore((state) => state.search.suggestions);
  const setSearchTerm = useUIStore((state) => state.setSearchTerm);
  const clearSearch = useUIStore((state) => state.clearSearch);
  const openSidebar = useUIStore((state) => state.openSidebar);
  const cartItemCount = useFinanceStore((state) => state.getCartItemCount?.());
  const activateCreatorTools = useDataStore((state) => state.activateCreatorTools);
  const [inputValue, setInputValue] = useState(searchTerm);
  const debouncedInputValue = useDebounce(inputValue, 500);
  const openModal = useUIStore((state) => state.openModal);

  const location = useLocation(); // Hook para obtener la ubicación actual
  useEffect(() => {
    if (debouncedInputValue) {
      setSearchTerm(debouncedInputValue);
    }
  }, [debouncedInputValue]);


  const hasNewSales = notifications.some(
    (n) => n.type === 'sale' && !n.isRead
  );
  const adminRoles = ['admin_root', 'admin', 'moderator', 'technician', 'editor'];
  const isAdminUser = Boolean(currentUser?.roles?.some((role) => adminRoles.includes(role)) || adminRoles.includes(currentUser?.role));
  const isAdminRoot = Boolean(currentUser?.roles?.includes('admin_root'));
  const hasBusinessProfile = Boolean(currentUser?.companies?.length || currentUser?.stores?.length);
  const adsCenterPath = isLoggedIn && hasBusinessProfile ? '/my-ads' : '/ads-center';

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isAdminNotificationOpen, setIsAdminNotificationOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false); // Estado para el dropdown de perfil
  const [isActivatingTools, setIsActivatingTools] = useState(false);
  const [isProvisioningFlowOpen, setIsProvisioningFlowOpen] = useState(false);
  const [isFinalizingIdentity, setIsFinalizingIdentity] = useState(false);
  const [provisioningTitle, setProvisioningTitle] = useState('Inicializando suite profesional...');
  const [provisioningSubtitle, setProvisioningSubtitle] = useState('Asignando módulos dinámicos de tu identidad.');

  // Refs para detectar clics fuera de los elementos
  const notificationRef = useRef(null);
  const adminNotificationRef = useRef(null);
  const searchRef = useRef(null);
  const profileRef = useRef(null); // Ref para el dropdown de perfil

  // Cierra los dropdowns si se hace clic fuera de ellos
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
      if (adminNotificationRef.current && !adminNotificationRef.current.contains(event.target)) {
        setIsAdminNotificationOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navigate = useNavigate();

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      if (searchTerm.trim()) {
        navigate(`/explore?q=${searchTerm.trim()}`);
      }
      setIsSearchOpen(false); // Cierra el buscador después de la búsqueda
      clearSearch(); // Limpia el término de búsqueda
    }
  };

  // Nueva función para cerrar el buscador y limpiar el término
  const handleCloseSearch = () => {
    setIsSearchOpen(false);
    clearSearch();
  };

  // Determina la ruta del perfil según el rol del usuario.
  const hasActiveTools = currentUser?.creatorToolsActive === undefined
    ? (currentUser?.role === 'blogger' || currentUser?.roles?.includes('blogger'))
    : currentUser?.creatorToolsActive;
  const profilePath = currentUser
    ? isAdminUser
      ? '/admin/root-profile'
      : hasActiveTools
      ? `/blogger/${currentUser?.username}`
      : `/profile/${currentUser?.username}`
    : '/auth';

  const isSenator = currentUser?.rank === 'senator' || currentUser?.tier === 'senador';
  const isPatricio = currentUser?.tier === 'patricio';
  const shouldShowActivation = !!currentUser && !hasActiveTools && (isSenator || isPatricio);
  const userRankLabel = isSenator ? 'Senador' : isPatricio ? 'Patricio' : 'Blogger';
  const handleActivateCreatorTools = async () => {
    if (isActivatingTools) return;
    setProvisioningTitle('Inicializando suite profesional...');
    setProvisioningSubtitle('Asignando módulos dinámicos de tu identidad.');
    setIsActivatingTools(true);
    const result = await activateCreatorTools();
    if (!result?.success) {
      setIsActivatingTools(false);
      return;
    }

    if (result?.requiresIdentitySelection) {
      setIsActivatingTools(false);
      setIsProfileOpen(false);
      setIsProvisioningFlowOpen(true);
      return;
    }

    setTimeout(() => {
      setIsActivatingTools(false);
      setIsProfileOpen(false);
      navigate(`/blogger/${currentUser?.username}`);
    }, 2500);
  };

  const handleSelectIdentity = async (identity) => {
    if (isFinalizingIdentity) return;

    const selectedProfileLabel = identityLabels[identity] || 'Perfil';
    setProvisioningTitle(`Configurando tu suite de ${selectedProfileLabel}...`);
    setProvisioningSubtitle('Aplicando permisos, módulos y entorno profesional.');

    const startedAt = Date.now();
    setIsFinalizingIdentity(true);
    setIsProvisioningFlowOpen(false);

    const result = await activateCreatorTools({ identity });
    if (result?.success) {
      const elapsed = Date.now() - startedAt;
      const minimumTransition = 800;
      if (elapsed < minimumTransition) {
        await sleep(minimumTransition - elapsed);
      }

      setIsProfileOpen(false);
      navigate(`/blogger/${currentUser?.username}`, { state: { agoraFadeIn: true } });
      await sleep(120);
    }

    setIsFinalizingIdentity(false);
    setProvisioningTitle('Inicializando suite profesional...');
    setProvisioningSubtitle('Asignando módulos dinámicos de tu identidad.');
  };

  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <header
      className="fixed z-50 w-full garcia translate-y-0"
    >
      <TopBar />
      <div className="bg-white dark:bg-gray-800 shadow-md randy h-[70px] transition-all duration-300">
        <div className="container mx-auto flex items-center justify-between h-full px-4">
          <div className="flex items-center">
            {isAdminUser && isAdminRoute && (
              <button
                onClick={openSidebar}
                className="mr-2 h-11 w-11 inline-flex items-center justify-center rounded-full text-gray-600 hover:text-gray-900 hover:bg-gray-100 lg:hidden"
                aria-label="Abrir menu"
              >
                <i className="fas fa-bars text-xl"></i>
              </button>
            )}
            <NavbarLogo />
          </div>
          {/* Los botones de la derecha ahora tienen más espacio */}
          <div className="flex items-center space-x-1 ml-auto">
            {!isAdminUser && (
              <>
                <Link
                  to="/stores"
                  // Ocultamos este botón en pantallas pequeñas para dar más espacio
                  className="h-[70px] w-16 hidden md:flex items-center justify-center border-x dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-zinc-600 hover:text-white transition-colors relative"
                  title="Tiendas"
                >
                  <i className="fas fa-store text-base"></i>
                  {/* Alerta azul para nuevas ventas */}
                  {hasNewSales && (
                    <span className="absolute top-4 right-3 flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                    </span>
                  )}
                </Link>
                <Link
                  to={adsCenterPath}
                  className="h-[70px] px-3 hidden md:flex items-center justify-center border-r dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-zinc-600 hover:text-white transition-colors text-sm font-semibold"
                  title={hasBusinessProfile ? 'Mi Ads Center' : 'Publicidad'}
                >
                  <i className="fas fa-bullhorn text-base mr-2"></i>
                  {hasBusinessProfile ? 'Mi Ads Center' : 'Publicidad'}
                </Link>
              </>
            )}
            {cartItemCount > 0 && (
              <Link
                to="/cart"
                className="h-[70px] w-16 flex items-center justify-center border-x dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-zinc-600 hover:text-white transition-colors relative"
                title="Carrito de Compras"
              >
                <i className="fas fa-shopping-cart text-base"></i>
                <span className="absolute top-4 right-1 flex h-5 w-5 bg-green-500 text-white text-xs items-center justify-center rounded-full">
                  {cartItemCount}
                </span>
              </Link>
            )}
            <div className="relative" ref={searchRef}>
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="h-[70px] w-16 flex items-center justify-center border-x dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-zinc-600 hover:text-white transition-colors"
              >
                <i className="fas fa-search text-base"></i>
              </button>
            </div>
            {currentUser ? ( // Si el usuario está logueado
              <div className="relative flex items-center space-x-4 pl-1" ref={profileRef}>
                {isAdminRoot && (
                  <div className="relative" ref={adminNotificationRef}>
                    <button
                      onClick={() => setIsAdminNotificationOpen(!isAdminNotificationOpen)}
                      className="h-[70px] w-16 flex items-center justify-center border-x dark:border-gray-700 text-amber-600 hover:bg-zinc-600 hover:text-white transition-colors"
                      title="Alertas financieras"
                    >
                      <i className="fas fa-bell text-base"></i>
                    </button>
                    {isAdminNotificationOpen && (
                      <AdminNotificationDropdown
                        onClose={() => setIsAdminNotificationOpen(false)}
                      />
                    )}
                  </div>
                )}
                {!isAdminRoot && (
                  <div className="relative" ref={notificationRef}>
                    <button
                      onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                      className="h-[70px] w-16 flex items-center justify-center border-x dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-zinc-600 hover:text-white transition-colors"
                    >
                      <i className="fas fa-bell text-base"></i>
                      {unreadCount > 0 && !isNotificationOpen && (
                        <span className="absolute top-4 right-1 flex h-5 w-5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-5 w-5 bg-red-500 text-white text-xs items-center justify-center">
                            {unreadCount}
                          </span>
                        </span>
                      )}
                    </button>
                    {isNotificationOpen && (
                      <NotificationDropdown
                        onClose={() => setIsNotificationOpen(false)}
                      />
                    )}
                  </div>
                )}
                {/* Botón del Avatar que abre el Dropdown de Perfil */}
                <button onClick={() => setIsProfileOpen(!isProfileOpen)} title={currentUser?.username}>
                  <img
                    src={getMediaUrl(currentUser?.avatar, DEFAULT_AVATAR_URL)}
                    alt="Avatar"
                    className={`h-9 w-9 rounded-full object-cover ${isSenator ? 'gold-aura' : ''}`}
                    onError={(event) => withImageFallback(event, DEFAULT_AVATAR_URL)}
                  />
                </button>

                {/* Dropdown de Perfil con animación */}
                <div
                  className={`absolute top-full right-0 mt-2 w-48 origin-top-right rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black/5 dark:ring-white/10 z-50 transition-all duration-200 ease-out transform ${
                    isProfileOpen
                      ? 'opacity-100 scale-100'
                      : 'opacity-0 scale-95 pointer-events-none'
                  }`}
                >
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{currentUser?.username}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{currentUser?.email}</p>
                  </div>
                  <div className="py-1">
                    <Link to={profilePath} onClick={() => setIsProfileOpen(false)} className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                      <i className="fas fa-user-circle w-5 mr-2"></i> Mi Perfil
                    </Link>
                    {isAdminUser ? (
                      <>
                        <Link to="/admin/settings" onClick={() => setIsProfileOpen(false)} className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                          <i className="fas fa-cogs w-5 mr-2"></i> Configuración
                        </Link>
                        <Link to="/admin/activity-log" onClick={() => setIsProfileOpen(false)} className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                          <i className="fas fa-history w-5 mr-2"></i> Log de Actividad
                        </Link>
                        <hr className="my-2 border-gray-200 dark:border-gray-700" />
                      </>
                    ) : null}
                    <button onClick={() => { logout(); setIsProfileOpen(false); }} className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                      <i className="fas fa-sign-out-alt w-5 mr-2"></i> Cerrar Sesión
                    </button>
                  </div>
                </div>
              </div>
            ) : ( // Si el usuario NO está logueado
              <button
                onClick={() => { openModal('loginModal'); }}
                className="h-[70px] w-16 flex items-center justify-center border-r dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
                title="Sign In / Register"
              >
                <i className="fas fa-user"></i>
              </button>
            )}
          </div>
        </div>
      </div>
      {isSearchOpen && (
        <div className="absolute top-full left-0 w-full bg-white dark:bg-gray-900 shadow-lg border-t border-gray-200 dark:border-gray-700">
          <div className="container mx-auto p-4 flex">
            <div className="relative w-full">
              <input
                type="text"
                placeholder="Buscar usuarios..."
                value={inputValue} // 🟢 Usar el estado local para el input
                onChange={(e) => setInputValue(e.target.value)} // 🟢 Actualizar el estado local
                onKeyDown={handleSearch}
                className="w-full bg-transparent text-gray-800 dark:text-gray-100 border-b-2 border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 py-2"
              />
              {searchTerm.length > 0 && (
                <ul className="absolute top-full left-0 w-full bg-white dark:bg-gray-900 rounded-b-lg shadow-lg max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 border-t-0">
                  {suggestions.map((user) => (
                    <li key={user._id}>
                      <Link
                        to={`/profile/${user.username}`}
                        onClick={handleCloseSearch} // <-- AÑADE EL EVENTO onClick
                        className="flex items-center space-x-3 p-3 text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800"
                      >
                        <img
                          src={user.avatar}
                          alt={user.username}
                          className="w-8 h-8 rounded-full"
                        />
                        <span>{user.username}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button className="text-gray-700 dark:text-gray-200 p-searh-p text-xl ml-2">
              <i className="fa fa-search"></i>
            </button>
          </div>
        </div>
      )}
      <CreatorActivationOverlay isOpen={isActivatingTools} rankLabel={userRankLabel} />
      <AgoraProvisioningTransition
        isOpen={isActivatingTools || isFinalizingIdentity}
        title={provisioningTitle}
        subtitle={provisioningSubtitle}
      />
      <CreatorIdentityProvisioningModal
        isOpen={isProvisioningFlowOpen}
        isSubmitting={isFinalizingIdentity}
        onClose={() => !isFinalizingIdentity && setIsProvisioningFlowOpen(false)}
        onSelect={handleSelectIdentity}
      />
    </header>
  );
};

export default Navbar;
